// @flow
import type {AbstractComponent, Element, Node} from 'react'
import React, {Component} from 'react'
import classNames from 'classnames'
import {createFragmentContainer, graphql} from 'react-relay'
import type {$RelayProps} from 'react-relay'
import ProposalContextProviderFragment from 'contexts/ProposalContextProviderFragment'
// eslint-disable-next-line import/named
import Alert, {Severity} from 'components/ui/Alert'
import EditorFooter from 'components/proposals/EditorFooter'
import ProposalSuggestion from 'components/ai/AiSuggest/ProposalSuggestion'
import ChangeList from '../ChangeList'
import TensionEditor from './TensionEditor'
import styles from './index.scss'
import type {
  Builder_meeting as Meeting,
} from './__generated__/Builder_meeting.graphql'
import type {
  Builder_organization as Organization,
} from './__generated__/Builder_organization.graphql'
import type {
  Builder_proposal as Proposal,
} from './__generated__/Builder_proposal.graphql'

type Props = {
  disabled: boolean,
  isHost: boolean,
  isModal: boolean,
  organization: Organization,
  proposal: Proposal,
  meeting: ?Meeting,
  updateOnUnmount: boolean,
  providedTension?: ?string,
}

class Builder extends Component<Props> {
  static defaultProps: { disabled: boolean, updateOnUnmount: boolean, isModal: boolean } = {
    disabled: false,
    updateOnUnmount: true,
    isModal: false,
  }

  get isReadonly(): boolean {
    return !this.props.isHost
  }

  get disabled(): boolean {
    return this.props.disabled || this.isReadonly
  }

  renderTensionEditor(): null | Node {
    const {meeting, organization, proposal} = this.props
    // if (this.props.providedTension && this.props.providedTension !== '')
    //   proposal.tension = this.props.providedTension

    if (!proposal.tension)
      return null

    return (
      <TensionEditor
        key={proposal.tension.id}
        isReadonly={this.disabled}
        meeting={meeting}
        organization={organization}
        proposal={proposal}
        tension={proposal.tension}
        updateOnUnmount={this.props.updateOnUnmount}
        providedTension={this.props.providedTension}
      />
    )
  }

  renderChangeList: (() => Node) = () => (
    <ChangeList
      isReadonly={this.disabled}
      meeting={this.props.meeting}
      organization={this.props.organization}
      proposal={this.props.proposal}
    />
  )

  // eslint-disable-next-line class-methods-use-this
  renderTranslationError: (() => Element<"div">) = () => (
    <div className={styles.proposalTranslationError}>
      <Alert
        message={I18n.t('proposals.rewrite.translation_error')}
        severity={Severity.Warning}
      />
    </div>
  )

  renderBody: (() => Node | Element<"div">) = () => {
    if (this.props.proposal.translationError)
      return this.renderTranslationError()

    if (!this.isReadonly) {
      return (
        <EditorFooter
          disabled={this.disabled}
          proposal={this.props.proposal}
          meeting={this.props.meeting}
        />
      )
    }

    return null
  }

  render(): Node {
    const formClass = classNames(styles.proposalForm, {
      [styles.modalForm]: this.props.isModal,
    })

    const proposal = this.props.proposal

    if (!this.props.proposal)
      return <div>{I18n.t('proposals.react.no_proposal')}</div>

    return (
      <ProposalContextProviderFragment proposal={this.props.proposal}>
        <div className={styles.proposalContainer}>
          <div className={formClass}>
            {this.renderTensionEditor()}
            {this.props.organization.isAiEnabled ? (
              <ProposalSuggestion
                orgDatabaseId={this.props.organization.databaseId}
                context={{type: 'proposal', id: proposal.databaseId}}
                promptKey="suggest_proposal_general"
                data={{
                  tensionBody: proposal.tension?.body || '',
                  circle: {ref_type: 'Circle', ref_id: proposal.circle?.databaseId},
                  circleId: proposal.circle?.databaseId,
                }}
              />
            ) : null}
            {this.renderChangeList()}
          </div>
          {this.renderBody()}
        </div>
      </ProposalContextProviderFragment>
    )
  }
}

export default (createFragmentContainer(Builder, {
  meeting: graphql`
    fragment Builder_meeting on Meeting {
      id
      databaseId

      ...ChangeList_meeting
      ...TensionEditor_meeting
      ...EditorFooter_meeting
    }
  `,
  proposal: graphql`
    fragment Builder_proposal on Proposal {
      databaseId      
      translationError
      ...ProposalContextProviderFragment_proposal

      circle {
        databaseId      
      }

      tension {
        id
        body
        databaseId
        ...TensionEditor_tension
      }

      ...TensionEditor_proposal
      ...ChangeList_proposal
      ...EditorFooter_proposal
    }
  `,
  organization: graphql`
    fragment Builder_organization on Organization {
      databaseId
      isAiEnabled
      ...ChangeList_organization
      ...TensionEditor_organization
    }
  `,
}): AbstractComponent<$RelayProps<React$ElementConfig<typeof Builder>>, Builder>)
